import { Bug, Calendar, Phone as PhoneIcon, Plus as PlusIcon, UserPlus, Xmark } from 'iconoir-react'
import { LockSimpleIcon } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import { ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import { ContactNameIcon } from '../contacts/ContactNameIcon'
import { ContactCardPipelines } from './ContactCardPipelines'
import { QuickCreateContactModal } from '../contacts/quick-create/QuickCreateContactModal'
import Spinner from '../Spinner'
import { Toggle } from '../Toggle'
import { AISparkle } from 'app/components/Icon/AIBadgeIcon'
import { CallInitiationModal } from 'app/components/click-to-call/CallInitiationModal'
import { OutboundCallProvider } from 'app/components/click-to-call/OutboundCallProvider'
import {
  ENGAGE_AGENT_MUTATION,
  HAS_ACTIVE_AGENT_QUERY,
  THREAD_QUERY,
  type HasActiveAgentResult,
} from 'app/components/inbox/graphql'
import { linkThreadMessagesToContact } from 'app/lib/linkThreadToContact'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { SchedulingDockContext } from 'app/components/inbox/scheduling-dock/SchedulingDockContext'
import { useCanSchedule } from 'app/components/inbox/scheduling-dock/useCanSchedule'
import PhoneNumber from 'app/components/PhoneNumber'
import { ScheduleAppointmentDialog } from 'app/components/scheduling/inapp/ScheduleAppointmentDialog'
import { ScheduleUnavailableNote } from 'app/components/scheduling/inapp/ScheduleUnavailableNote'
import { Popover, PopoverAnchor, PopoverContent } from 'app/components/ui/popover'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import type { ContactJson } from 'app/types/entities'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useInboxDebugMode } from 'app/hooks/useInboxDebugMode'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

const ENGAGE_FAILURE_REASONS: Readonly<Record<string, string>> = {
  NO_CONTACT: `Link this conversation to a contact first.`,
  LOCATION_NOT_CONFIGURED: `This location doesn't have the SMS Agent product.`,
  AGENT_TOGGLE_DISABLED: `The SMS agent isn't turned on for this workspace.`,
  AGENT_PROMPT_MISSING: `This location has no agent prompt deployed.`,
  NO_MESSAGES: `There's no text here for the agent to answer.`,
}

function useRelatedContacts({
  cellPhone,
  callerId,
  contactName,
  relatedContacts,
  threadId,
  onContactCreated,
  onAgentChange,
}: {
  cellPhone?: string
  callerId?: string
  contactName?: string
  relatedContacts?: ContactJson[]
  threadId?: string
  onContactCreated?: (contact: ContactJson) => void
  onAgentChange?: (enabled: boolean) => void
}) {
  const me = useUser()
  // Super-user only: AI is trigger-based for everyone else, so the right-panel
  // override is hidden from regular users. They can still take over via the
  // in-message banner (which has its own confirm).
  const isSuperUser = me?.type === `super`
  const [contacts, setContacts] = useState<ContactJson[]>(relatedContacts ?? [])
  // Optimistic override after a successful toggle. While unset, we render the
  // server's value verbatim — never a default. A default would diverge from
  // PageContent's banner, which derives from the same Thread.aiEnabled.
  const [agentEnabledOverride, setAgentEnabledOverride] = useState<boolean | undefined>(undefined)
  const [engageError, setEngageError] = useState<string | undefined>(undefined)
  const [searchParams] = useSearchParams()
  const locationId = searchParams.get(`locationId`) ?? undefined

  const opsContext = useMemo(() => ({ url: OPS_URL }), [])
  const [agentQuery] = useQuery<HasActiveAgentResult>({
    query: HAS_ACTIVE_AGENT_QUERY,
    variables: { locationId },
    pause: !isSuperUser || !locationId,
    context: opsContext,
  })
  const hasActiveAgent: boolean | undefined =
    !isSuperUser || !locationId || agentQuery.fetching || agentQuery.error
      ? agentQuery.error
        ? false
        : undefined
      : (agentQuery.data?.aiAgentConfigurations?.some((c) => !!c.systemPrompt?.trim()) ?? false)

  const [threadQuery] = useQuery<{ thread: { id: string; aiEnabled: boolean; name?: string | null } | null }>({
    query: THREAD_QUERY,
    variables: { id: threadId },
    pause: !threadId || !isSuperUser,
  })

  // Reset the optimistic override whenever the server returns a fresh value, so
  // we never show a stale opinion after a refetch wins.
  useEffect(() => {
    if (threadQuery.data) setAgentEnabledOverride(undefined)
  }, [threadQuery.data])

  // Until the query lands, defer to undefined so we don't paint a default that
  // contradicts the eventual server value.
  const serverAgentEnabled = threadQuery.data?.thread?.aiEnabled
  const agentEnabled = agentEnabledOverride ?? (threadQuery.data?.thread ? serverAgentEnabled === true : undefined)
  // Conversations created before the threadId migration have no threadId on
  // their Message rows, so we have nothing to query/update. Hide the toggle in
  // that case rather than show a dead control.
  const canToggleAgent = !!threadId

  useEffect(() => setEngageError(undefined), [threadId])

  const [{ fetching: engaging }, engageAgentMutation] = useMutation(ENGAGE_AGENT_MUTATION)

  const engageAgent = async () => {
    if (!threadId) return
    setEngageError(undefined)
    const result = await engageAgentMutation({ threadId }, { suppressErrorToast: true })
    if (result.error || !result.data?.threadEngageAgent) {
      const failure = result.error?.graphQLErrors[0]
      const code = failure?.extensions?.code
      setEngageError(
        (typeof code === `string` ? ENGAGE_FAILURE_REASONS[code] : undefined) ??
          failure?.message ??
          `Couldn't engage the AI Agent. Try again.`,
      )
      logError(`threadEngageAgent failed`, result.error?.message)
      return
    }
    setAgentEnabledOverride(true)
    onAgentChange?.(true)
  }

  const handleNewContact = async (newContact: ContactJson) => {
    if (!threadId && newContact.cellPhone !== cellPhone) return
    if (threadId && newContact.id) {
      try {
        // Sweeps every message in the thread (paged), fills only unattributed
        // ones, and retries failures once — see linkThreadMessagesToContact.
        // (Passing the thread id straight to linkMessageToContact does not
        // work: the resolver looks the id up as a single Message.)
        const { linked, total, failed } = await linkThreadMessagesToContact(threadId, newContact.id)
        if (failed > 0) {
          // Partial link: the contact exists and most messages are attributed.
          // Don't abort the local update over the remainder; report it honestly.
          notifyError(`Linked ${linked} of ${total} conversation messages to the new contact`)
          logError(`Partial conversation link`, { threadId, contactId: newContact.id, linked, total, failed })
        }
      } catch (err) {
        notifyError(`Failed to update conversation with new contact`)
        logError(`Error linking conversation to new contact`, err)
        return
      }
    }
    setContacts([newContact])

    onContactCreated?.(newContact)
  }

  const openCreateContact = () => {
    showModal(
      <QuickCreateContactModal
        defaultCellPhone={cellPhone}
        onCreate={(contact) => bg(() => handleNewContact(contact))}
      />,
      { hideTitle: true, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
    )
  }

  useEffect(() => setContacts(relatedContacts ?? []), [relatedContacts])

  const callerDisplayName = callerId

  return {
    contacts,
    callerDisplayName,
    isSuperUser,
    agentEnabled,
    hasActiveAgent,
    canToggleAgent,
    engageAgent,
    engaging,
    engageError,
    openCreateContact,
  }
}

export function NumberInfoHeader({ onClose }: { onClose?: () => void }): ReactElement {
  return (
    <div className="flex h-app-header w-full shrink-0 items-center gap-x-2 bg-surface-subtle px-4">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate text-heading-sm font-semibold text-strong">About this number</span>
      </div>
      {onClose && (
        <TooltipWrapper placement="bottom-end" message="Close">
          <button
            type="button"
            aria-label="Close details"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-muted hover:text-default"
          >
            <Xmark className="h-5 w-5" strokeWidth={2} />
          </button>
        </TooltipWrapper>
      )}
    </div>
  )
}

export const RelatedContactCard = observer(function RelatedContactCard({
  contact,
  onSelect,
}: {
  contact: ContactJson
  onSelect?: (contact: ContactJson) => void
}): ReactElement {
  const { openDock, location } = useContext(SchedulingDockContext)
  const { state: scheduleState, reason: scheduleReason } = useCanSchedule(contact)
  const isMobile = useIsMobile()
  // Below md there is no dock: the same wizard opens as a full-screen dialog.
  const [mobileScheduleOpen, setMobileScheduleOpen] = useState(false)
  const [scheduleNoteOpen, setScheduleNoteOpen] = useState(false)

  // Location hasn't loaded yet, so mirror Schedule's `pending` treatment (disabled,
  // no reason shown) rather than flashing a "no outbound number" tooltip that
  // would be wrong once the location arrives.
  const callPending = !location
  const canCall = !!location?.primaryTrackingNumber

  const handleCall = () => {
    showModal(
      <OutboundCallProvider>
        <CallInitiationModal contact={contact} />
      </OutboundCallProvider>,
      {
        title: `Outbound Call`,
        isFullScreen: false,
        removePadding: true,
        closable: true,
        showMinimizeButton: false,
        useRadix: true,
      },
    )
  }

  // Locked and blocked stay clickable and say why where the user tried, rather than sitting greyed out.
  const scheduleButton = (
    <CardActionButton
      icon={
        scheduleState === `locked` ? (
          <LockSimpleIcon className="h-3.5 w-3.5" />
        ) : (
          <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
        )
      }
      label="Schedule"
      disabled={scheduleState === `pending`}
      emphasize={scheduleState === `ready`}
      onClick={() => {
        if (scheduleState !== `ready`) setScheduleNoteOpen(true)
        else if (isMobile) setMobileScheduleOpen(true)
        else openDock(contact)
      }}
    />
  )

  const callButton = (
    <CardActionButton
      icon={<PhoneIcon className="h-3.5 w-3.5" strokeWidth={2} />}
      label="Call"
      disabled={!canCall}
      onClick={handleCall}
    />
  )

  // Card is a container (not a single <button>) so the pipelines block and action
  // row below can hold their own interactive controls. The top row remains a
  // clickable "open contact" hit area, even though Open now also lives in the
  // action row as an explicit button.
  return (
    <div className="rounded-lg border border-default bg-surface transition-colors focus-within:border-primary-300 hover:border-primary-300">
      <button
        type="button"
        onClick={(evt) => {
          evt.preventDefault()
          onSelect?.(contact)
        }}
        className="flex w-full items-center gap-3 rounded-t-lg p-3 text-left transition-colors hover:bg-primary-50/30"
      >
        <ContactNameIcon contact={contact} widthClassName="w-9" heightClassName="h-9" sizeStyle={{ fontSize: 12 }} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-body-sm font-semibold text-strong">{contact.name}</div>
          {(contact.lifecycleStage || contact.leadStatus) && (
            <div className="mt-0.5 flex items-center gap-x-1.5 truncate text-label-xs">
              {contact.lifecycleStage && <span className="font-medium text-green-600">{contact.lifecycleStage}</span>}
              {contact.lifecycleStage && contact.leadStatus && <span className="text-minimal">·</span>}
              {contact.leadStatus && <span className="text-subtle">{contact.leadStatus}</span>}
            </div>
          )}
        </div>
      </button>
      <div className="px-3 pb-2">
        <ContactCardPipelines contact={contact} />
      </div>
      {/* Subtle grey bar so the actions read as a tray under the card body, with
          the buttons as white badges sitting on it. */}
      <div className="flex items-stretch gap-x-1 rounded-b-lg border-t border-default bg-surface-subtle px-2 py-1.5">
        {scheduleReason ? (
          <Popover open={scheduleNoteOpen} onOpenChange={setScheduleNoteOpen}>
            <PopoverAnchor asChild>
              <div className="flex flex-1">{scheduleButton}</div>
            </PopoverAnchor>
            <PopoverContent side="top" className="w-64 p-3">
              <ScheduleUnavailableNote locked={scheduleState === `locked`} reason={scheduleReason} />
            </PopoverContent>
          </Popover>
        ) : (
          scheduleState !== `hidden` && scheduleButton
        )}
        {canCall || callPending ? (
          callButton
        ) : (
          <TooltipWrapper placement="top" message="This location has no outbound number configured" className="flex-1">
            {callButton}
          </TooltipWrapper>
        )}
        <CardActionButton
          icon={<OpenIcon className="h-3.5 w-3.5" />}
          label="Open"
          onClick={() => onSelect?.(contact)}
        />
      </div>
      {isMobile && contact.locationId && (
        <ScheduleAppointmentDialog
          contact={contact}
          locationId={contact.locationId}
          kollaConsumerId={location?.kollaConsumerId ?? undefined}
          open={mobileScheduleOpen}
          onOpenChange={setMobileScheduleOpen}
        />
      )}
    </div>
  )
})

/**
 * A compact icon+label action for the card's bottom toolbar row. `emphasize`
 * gives the most important action (Schedule) a primary tint while keeping the
 * same borderless, same-size treatment as its peers, so the row reads as a
 * toolbar of equals rather than one primary button among plain ones.
 */
function CardActionButton({
  icon,
  label,
  onClick,
  disabled,
  emphasize,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
  emphasize?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      // White badge button on the card's flat bg-neutral-50 action bar, so each
      // action reads as its own control rather than the bar itself being a button.
      className={`flex flex-1 items-center justify-center gap-x-1.5 rounded-md border border-default bg-surface px-2 py-1.5 text-label-xs font-medium shadow-elevation-1 transition-colors hover:border-primary-300 hover:bg-primary-50/40 disabled:cursor-not-allowed disabled:border-default disabled:bg-surface/60 disabled:text-minimal disabled:shadow-none disabled:hover:border-default disabled:hover:bg-surface/60 ${
        emphasize ? `text-primary-600` : `text-subtle`
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

function OpenIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
    >
      <path
        d="M5.33333 14H13.6C13.8209 14 14 13.8209 14 13.6V2.4C14 2.17909 13.8209 2 13.6 2H2.4C2.17909 2 2 2.17909 2 2.4V10.6667"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.33398 13.6667L8.00065 8M8.00065 8V10.6667M8.00065 8H5.33398"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function CallSidebarBody({
  cellPhone,
  callerDisplayName,
  showAgentControl = false,
  showDebugControl = false,
  agentEnabled,
  hasPriorAIWorkflow = false,
  onEngageAgent,
  engaging = false,
  engageError,
  isLoading,
  contacts,
  onContactSelect,
  onAddContact,
}: {
  cellPhone?: string
  callerDisplayName?: ReactNode
  showAgentControl?: boolean
  // Super-user only: shows the inbox debug-mode toggle. Independent of agent
  // activity so a super user can flip debug on for any thread.
  showDebugControl?: boolean
  // undefined = unknown (no Thread row yet) — render nothing rather than guess.
  agentEnabled?: boolean
  // True if this thread has any message with aiWorkflowId set. When false AND
  // the toggle is off, turning it on would be a no-op (the SMS receive handler
  // discovers the workflow association from message history), so we disable the
  // toggle and explain why.
  hasPriorAIWorkflow?: boolean
  onEngageAgent?: () => void
  engaging?: boolean
  engageError?: string
  isLoading?: boolean
  contacts: ContactJson[]
  onContactSelect?: (contact: ContactJson) => void
  onAddContact?: () => void
}): ReactElement {
  // Once the agent is on, the composer owns its status and the turn-off for everyone.
  const showAgentCard = showAgentControl && agentEnabled === false
  const { debugMode, setDebugMode } = useInboxDebugMode()
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
      {/* Phone summary card */}
      <div className="flex items-center gap-3 rounded-lg border border-default bg-surface p-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-500">
          <PhoneIcon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-body-sm font-semibold text-strong">
            <PhoneNumber number={cellPhone} />
          </div>
          <div className="truncate text-label-xs text-subtle">
            {callerDisplayName ? <>Caller ID · {callerDisplayName}</> : `No Caller ID`}
          </div>
        </div>
      </div>

      {showAgentCard && (
        <div
          data-testid="ai-agent-engage-card"
          className="flex flex-col gap-2 rounded-lg border border-default bg-surface p-3"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50">
              <AISparkle gradientId="ai-agent-card-sparkle" className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-body-sm font-medium text-strong">AI Agent</div>
              <div className="truncate text-label-xs text-subtle">Replies to the latest text now</div>
            </div>
            <Button
              type="button"
              variant="white"
              size="sm"
              shape="pill"
              data-testid="ai-agent-engage"
              disabled={engaging}
              onClick={() => onEngageAgent?.()}
            >
              {engaging ? `Engaging…` : `Engage`}
            </Button>
          </div>
          {engageError && (
            <p role="alert" data-testid="ai-agent-engage-error" className="text-label-xs text-danger-default">
              {engageError}
            </p>
          )}
        </div>
      )}

      {/* Debug card — super-user only. Enables inbox debug mode, which renders the */}
      {/* AI agent's tool calls/results inline in the conversation. Persisted in */}
      {/* localStorage so it stays on across threads until turned off. */}
      {showDebugControl && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-400 bg-amber-50/50 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
            <Bug className="h-4 w-4" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-body-sm font-medium text-strong">Debug</div>
            <div className="truncate text-label-xs text-subtle">Enable debug mode</div>
          </div>
          <Toggle defaultValue={debugMode} handleToggle={(val) => setDebugMode(val)} />
        </div>
      )}

      {/* Related contacts section */}
      <div className="flex min-h-0 flex-1 flex-col gap-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-x-2">
            <span className="text-label-xs font-semibold uppercase tracking-wide text-subtle">
              Contacts with this number
            </span>
            {!isLoading && contacts.length > 0 && <span className="text-label-xs text-minimal">{contacts.length}</span>}
          </div>
          {onAddContact && (
            <TooltipWrapper placement="bottom-end" message="Add a contact">
              <button
                type="button"
                aria-label="Add a contact"
                onClick={onAddContact}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-primary-500 bg-surface text-primary-500 transition-colors hover:bg-primary-50"
              >
                <PlusIcon className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </TooltipWrapper>
          )}
        </div>

        <div className="flex min-h-0 flex-col gap-y-2 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-24 w-full items-center justify-center">
              <Spinner size={8} />
            </div>
          ) : contacts.length > 0 ? (
            contacts.map((contact, i) => (
              <RelatedContactCard key={`${contact.id}-${i}`} contact={contact} onSelect={onContactSelect} />
            ))
          ) : (
            <button
              type="button"
              onClick={onAddContact}
              className="flex flex-col items-center justify-center gap-y-2 rounded-lg border border-dashed border-default bg-surface px-4 py-6 text-center text-subtle transition-colors hover:border-primary-300 hover:bg-primary-50/30 hover:text-primary-600"
            >
              <UserPlus className="h-5 w-5" strokeWidth={2} />
              <div className="text-body-sm font-medium">Create a contact</div>
              <div className="text-label-xs text-minimal">No contacts associated with this number.</div>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type RelatedContactsComponentProps = {
  cellPhone?: string
  callerId?: string
  contactName?: string
  isLoading?: boolean
  relatedContacts?: ContactJson[]
  threadId?: string
  hasSmsAgentProduct?: boolean
  hasPriorAIWorkflow?: boolean
  onContactCreated?: (contact: ContactJson) => void
  onContactSelect?: (contact: ContactJson) => void
  onAgentChange?: (enabled: boolean) => void
  onClose?: () => void
} & ReturnType<typeof useRelatedContacts>

const RelatedContactsComponent = observer(function RelatedContactsComponent({
  cellPhone,
  isLoading,
  onContactSelect,
  contacts,
  callerDisplayName,
  isSuperUser,
  agentEnabled,
  hasActiveAgent,
  canToggleAgent,
  hasSmsAgentProduct = false,
  hasPriorAIWorkflow,
  engageAgent,
  engaging,
  engageError,
  openCreateContact,
  onClose,
}: RelatedContactsComponentProps): ReactElement {
  return (
    <div className="flex h-dvh w-full shrink-0 flex-col divide-y divide-subtle border-l border-l-default bg-surface-subtle transition-all md:h-screen md:w-[380px]">
      <NumberInfoHeader onClose={onClose} />
      <CallSidebarBody
        cellPhone={cellPhone}
        callerDisplayName={callerDisplayName}
        showAgentControl={isSuperUser && hasSmsAgentProduct && hasActiveAgent === true && canToggleAgent}
        showDebugControl={isSuperUser}
        agentEnabled={agentEnabled}
        hasPriorAIWorkflow={hasPriorAIWorkflow}
        onEngageAgent={() => bg(engageAgent)}
        engaging={engaging}
        engageError={engageError}
        isLoading={isLoading}
        contacts={contacts}
        onContactSelect={onContactSelect}
        onAddContact={openCreateContact}
      />
    </div>
  )
})

function RelatedContacts(props: Omit<RelatedContactsComponentProps, keyof ReturnType<typeof useRelatedContacts>>) {
  const hookState = useRelatedContacts({
    cellPhone: props.cellPhone,
    callerId: props.callerId,
    contactName: props.contactName,
    relatedContacts: props.relatedContacts,
    threadId: props.threadId,
    onContactCreated: props.onContactCreated,
    onAgentChange: props.onAgentChange,
  })
  return <RelatedContactsComponent {...props} {...hookState} />
}

export { RelatedContacts }
Object.assign(RelatedContacts, { useRelatedContacts })
